import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChevronLeft, Loader2, Search, Briefcase, MapPin, 
  Star, Trash2, Pencil, Plus, Eye, Award, Percent
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  pro: "#4A90D9", elite: "#9B59B6", master: "#F1C40F",
  grandmaster: "#E74C3C", legendary: "#E07A5F"
};
const RANKS = ['pro', 'elite', 'master', 'grandmaster', 'legendary'];
const VERIF_STATUS = [
  { value: 'pending', label: 'En attente', cls: 'bg-amber-100 text-amber-800' },
  { value: 'verified', label: 'Vérifié', cls: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Rejeté', cls: 'bg-red-100 text-red-800' },
];

export default function AdminProviders() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [editProvider, setEditProvider] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [globalCommission, setGlobalCommission] = useState(0.15);
  const [customCommission, setCustomCommission] = useState({ enabled: false, rate: 15 });

  // Create form
  const [newProvider, setNewProvider] = useState({
    name: '', email: '', password: 'Temp123!', phone: '',
    service_category: 'chef', bio: '', hourly_rate: 30,
    country: 'DE', city: 'Hamburg', rank: 'pro',
    verification_status: 'verified'
  });

  const auth = { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const [provRes, catRes, commRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/providers`, { headers: auth }),
        fetch(`${API_URL}/api/config/categories`),
        fetch(`${API_URL}/api/admin/commission`, { headers: auth })
      ]);
      if (provRes.ok) {
        const data = await provRes.json();
        setProviders(data.providers || []);
      }
      if (catRes.ok) setCategories((await catRes.json()).categories || {});
      if (commRes.ok) {
        const commData = await commRes.json();
        setGlobalCommission(commData.commission_rate || 0.15);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProviders(); }, []);

  const filtered = providers.filter(p => {
    const matchSearch = !search || 
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || p.verification_status === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── DELETE ──
  const handleDelete = async (provider) => {
    if (!window.confirm(`Supprimer ${provider.name} et son compte utilisateur ? Cette action est irréversible.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/providers/${provider.provider_id}`, {
        method: 'DELETE', headers: auth
      });
      if (res.ok) { toast.success('Prestataire supprimé'); fetchProviders(); }
      else toast.error('Erreur lors de la suppression');
    } catch (e) { toast.error('Erreur'); }
  };

  // ── EDIT (open dialog) ──
  const openEdit = (provider) => {
    setEditProvider({ ...provider });
    setEditOpen(true);
  };

  // ── SAVE EDIT ──
  const saveEdit = async () => {
    setSaving(true);
    try {
      // Update profile fields
      const profileRes = await fetch(`${API_URL}/api/admin/providers/${editProvider.provider_id}/profile`, {
        method: 'PUT',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editProvider.name,
          email: editProvider.email,
          phone: editProvider.phone,
          bio: editProvider.bio,
          service_category: editProvider.service_category,
          hourly_rate: parseFloat(editProvider.hourly_rate) || 30,
          country: editProvider.country,
          city: editProvider.city,
          is_available: editProvider.is_available,
        })
      });

      // Update rank if changed
      const origProvider = providers.find(p => p.provider_id === editProvider.provider_id);
      if (origProvider?.rank !== editProvider.rank) {
        await fetch(`${API_URL}/api/admin/providers/${editProvider.provider_id}/rank`, {
          method: 'PUT',
          headers: { ...auth, 'Content-Type': 'application/json' },
          body: JSON.stringify({ rank: editProvider.rank })
        });
      }

      // Update verification status if changed
      if (origProvider?.verification_status !== editProvider.verification_status) {
        await fetch(`${API_URL}/api/admin/providers/${editProvider.provider_id}/verification`, {
          method: 'PUT',
          headers: { ...auth, 'Content-Type': 'application/json' },
          body: JSON.stringify({ verification_status: editProvider.verification_status })
        });
      }

      if (profileRes.ok) {
        toast.success('Profil mis à jour');
        setEditOpen(false);
        fetchProviders();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // ── CREATE ──
  const handleCreate = async () => {
    if (!newProvider.name || !newProvider.email) { toast.error('Nom et email requis'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/providers`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(newProvider)
      });
      if (res.ok) {
        toast.success('Prestataire créé');
        setCreateOpen(false);
        setNewProvider({ name: '', email: '', password: 'Temp123!', phone: '', service_category: 'chef', bio: '', hourly_rate: 30, country: 'DE', city: 'Hamburg', rank: 'pro', verification_status: 'verified' });
        fetchProviders();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  // ── Quick rank change ──
  const quickChangeRank = async (providerId, newRank) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/providers/${providerId}/rank`, {
        method: 'PUT',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rank: newRank })
      });
      if (res.ok) { toast.success(`Rang mis à jour: ${newRank}`); fetchProviders(); }
    } catch (e) { toast.error('Erreur'); }
  };

  // ── Quick verification change ──
  const quickChangeVerification = async (providerId, newStatus) => {
    const endpoint = newStatus === 'verified' 
      ? `${API_URL}/api/admin/verifications/${providerId}/approve`
      : `${API_URL}/api/admin/providers/${providerId}/verification`;
    
    const res = await fetch(endpoint, {
      method: 'PUT',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ verification_status: newStatus })
    });
    if (res.ok) { toast.success('Statut mis à jour'); fetchProviders(); }
  };

  // ── Open commission dialog ──
  const openCommissionDialog = (provider) => {
    setEditProvider(provider);
    const hasCustom = provider.custom_commission_rate !== null && provider.custom_commission_rate !== undefined;
    setCustomCommission({
      enabled: hasCustom,
      rate: hasCustom ? provider.custom_commission_rate * 100 : globalCommission * 100
    });
    setCommissionDialogOpen(true);
  };

  // ── Save commission ──
  const saveCommission = async () => {
    if (!editProvider) return;
    setSaving(true);
    try {
      const customRate = customCommission.enabled ? customCommission.rate / 100 : null;
      
      const res = await fetch(`${API_URL}/api/admin/providers/${editProvider.provider_id}/commission`, {
        method: 'PUT',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_commission_rate: customRate })
      });

      if (res.ok) {
        toast.success(customCommission.enabled ? 'Commission personnalisée appliquée' : 'Commission globale restaurée');
        setCommissionDialogOpen(false);
        fetchProviders();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const getVerifCls = (status) => VERIF_STATUS.find(v => v.value === status)?.cls || 'bg-gray-100 text-gray-800';
  const getVerifLabel = (status) => VERIF_STATUS.find(v => v.value === status)?.label || status;

  if (loading) {
    return <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-providers">
      <div className="container-app max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Gestion des prestataires</h1>
              <p className="text-muted-foreground text-sm">{providers.length} prestataires</p>
            </div>
          </div>
          <Button className="btn-primary" onClick={() => setCreateOpen(true)} data-testid="create-provider-btn">
            <Plus className="w-4 h-4 mr-2" />Créer un prestataire
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-providers" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="status-filter">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="verified">Vérifié</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="rejected">Rejeté</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Provider List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16"><Briefcase className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" /><p className="text-muted-foreground">Aucun prestataire trouvé</p></div>
        ) : (
          <div className="space-y-3">
            {filtered.map(provider => (
              <Card key={provider.provider_id} data-testid={`provider-row-${provider.provider_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12 flex-shrink-0">
                      {provider.profile_image ? <AvatarImage src={provider.profile_image} /> : null}
                      <AvatarFallback className="bg-accent text-accent-foreground">{provider.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{provider.name}</p>
                        {/* Rank selector */}
                        <Select value={provider.rank} onValueChange={(val) => quickChangeRank(provider.provider_id, val)}>
                          <SelectTrigger className="h-6 w-auto px-2 border-0 gap-1" style={{ color: RANK_COLORS[provider.rank] }} data-testid={`rank-select-${provider.provider_id}`}>
                            <Award className="w-3 h-3" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RANKS.map(r => <SelectItem key={r} value={r}><span style={{ color: RANK_COLORS[r] }}>{r.charAt(0).toUpperCase() + r.slice(1)}</span></SelectItem>)}
                          </SelectContent>
                        </Select>
                        {/* Verification status selector */}
                        <Select value={provider.verification_status || 'pending'} onValueChange={(val) => quickChangeVerification(provider.provider_id, val)}>
                          <SelectTrigger className={`h-6 w-auto px-2 border-0 rounded-full text-xs font-medium ${getVerifCls(provider.verification_status)}`} data-testid={`verif-select-${provider.provider_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VERIF_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-sm text-muted-foreground">{provider.email}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{provider.service_category}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{provider.city || 'N/A'}</span>
                        <span className="flex items-center gap-1"><Star className="w-3 h-3" />{provider.rating?.toFixed(1) || '0.0'}</span>
                        <span>{provider.hourly_rate}€/h</span>
                        <span className="flex items-center gap-1">
                          <Percent className="w-3 h-3" />
                          {provider.custom_commission_rate !== null && provider.custom_commission_rate !== undefined 
                            ? <span className="text-amber-600 font-medium">{(provider.custom_commission_rate * 100).toFixed(0)}%</span>
                            : <span>{(globalCommission * 100).toFixed(0)}%</span>
                          }
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openCommissionDialog(provider)} title="Commission" data-testid={`commission-btn-${provider.provider_id}`}>
                        <Percent className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(provider)} data-testid={`edit-btn-${provider.provider_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => handleDelete(provider)} data-testid={`delete-btn-${provider.provider_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ── EDIT DIALOG ── */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier le profil</DialogTitle>
            </DialogHeader>
            {editProvider && (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Nom</Label><Input value={editProvider.name || ''} onChange={e => setEditProvider(p => ({...p, name: e.target.value}))} data-testid="edit-name" /></div>
                  <div className="space-y-1"><Label>Email</Label><Input value={editProvider.email || ''} onChange={e => setEditProvider(p => ({...p, email: e.target.value}))} data-testid="edit-email" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Téléphone</Label><Input value={editProvider.phone || ''} onChange={e => setEditProvider(p => ({...p, phone: e.target.value}))} /></div>
                  <div className="space-y-1"><Label>Tarif horaire (€)</Label><Input type="number" value={editProvider.hourly_rate || 30} onChange={e => setEditProvider(p => ({...p, hourly_rate: e.target.value}))} data-testid="edit-rate" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Catégorie</Label>
                    <Select value={editProvider.service_category} onValueChange={val => setEditProvider(p => ({...p, service_category: val}))}>
                      <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(categories).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Rang</Label>
                    <Select value={editProvider.rank} onValueChange={val => setEditProvider(p => ({...p, rank: val}))}>
                      <SelectTrigger data-testid="edit-rank"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RANKS.map(r => <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Pays</Label><Input value={editProvider.country || ''} onChange={e => setEditProvider(p => ({...p, country: e.target.value}))} /></div>
                  <div className="space-y-1"><Label>Ville</Label><Input value={editProvider.city || ''} onChange={e => setEditProvider(p => ({...p, city: e.target.value}))} /></div>
                </div>
                <div className="space-y-1">
                  <Label>Statut de vérification</Label>
                  <Select value={editProvider.verification_status || 'pending'} onValueChange={val => setEditProvider(p => ({...p, verification_status: val}))}>
                    <SelectTrigger data-testid="edit-verif"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VERIF_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Bio</Label><Textarea value={editProvider.bio || ''} onChange={e => setEditProvider(p => ({...p, bio: e.target.value}))} className="min-h-[80px]" /></div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setEditOpen(false)}>Annuler</Button>
                  <Button className="btn-primary flex-1" onClick={saveEdit} disabled={saving} data-testid="save-edit-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ── CREATE DIALOG ── */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer un prestataire</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Nom *</Label><Input value={newProvider.name} onChange={e => setNewProvider(p => ({...p, name: e.target.value}))} data-testid="create-name" /></div>
                <div className="space-y-1"><Label>Email *</Label><Input value={newProvider.email} onChange={e => setNewProvider(p => ({...p, email: e.target.value}))} data-testid="create-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Mot de passe</Label><Input value={newProvider.password} onChange={e => setNewProvider(p => ({...p, password: e.target.value}))} /></div>
                <div className="space-y-1"><Label>Téléphone</Label><Input value={newProvider.phone} onChange={e => setNewProvider(p => ({...p, phone: e.target.value}))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Catégorie</Label>
                  <Select value={newProvider.service_category} onValueChange={val => setNewProvider(p => ({...p, service_category: val}))}>
                    <SelectTrigger data-testid="create-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(categories).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Tarif horaire (€)</Label><Input type="number" value={newProvider.hourly_rate} onChange={e => setNewProvider(p => ({...p, hourly_rate: parseFloat(e.target.value) || 30}))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Rang</Label>
                  <Select value={newProvider.rank} onValueChange={val => setNewProvider(p => ({...p, rank: val}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RANKS.map(r => <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Vérification</Label>
                  <Select value={newProvider.verification_status} onValueChange={val => setNewProvider(p => ({...p, verification_status: val}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VERIF_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Pays</Label><Input value={newProvider.country} onChange={e => setNewProvider(p => ({...p, country: e.target.value}))} /></div>
                <div className="space-y-1"><Label>Ville</Label><Input value={newProvider.city} onChange={e => setNewProvider(p => ({...p, city: e.target.value}))} /></div>
              </div>
              <div className="space-y-1"><Label>Bio</Label><Textarea value={newProvider.bio} onChange={e => setNewProvider(p => ({...p, bio: e.target.value}))} /></div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCreateOpen(false)}>Annuler</Button>
                <Button className="btn-primary flex-1" onClick={handleCreate} disabled={saving} data-testid="create-submit-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer le prestataire'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── COMMISSION DIALOG ── */}
        <Dialog open={commissionDialogOpen} onOpenChange={setCommissionDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-accent" />
                Commission pour {editProvider?.name}
              </DialogTitle>
              <DialogDescription>
                Définir un taux de commission personnalisé pour ce prestataire
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Taux global actuel</p>
                <p className="text-2xl font-bold">{(globalCommission * 100).toFixed(0)}%</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="custom-commission" className="font-medium">Commission personnalisée</Label>
                  <p className="text-sm text-muted-foreground">Remplace le taux global pour ce prestataire</p>
                </div>
                <Switch
                  id="custom-commission"
                  checked={customCommission.enabled}
                  onCheckedChange={(checked) => setCustomCommission(prev => ({ ...prev, enabled: checked }))}
                  data-testid="custom-commission-switch"
                />
              </div>

              {customCommission.enabled && (
                <div className="space-y-2">
                  <Label htmlFor="commission-rate">Taux de commission (%)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="commission-rate"
                      type="number"
                      min="0"
                      max="100"
                      value={customCommission.rate}
                      onChange={(e) => setCustomCommission(prev => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                      className="flex-1"
                      data-testid="custom-commission-input"
                    />
                    <span className="text-lg font-medium">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Le client paiera {editProvider?.hourly_rate}€ + {customCommission.rate}% = {(editProvider?.hourly_rate * (1 + customCommission.rate / 100)).toFixed(2)}€/h
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCommissionDialogOpen(false)}>
                  Annuler
                </Button>
                <Button className="btn-primary flex-1" onClick={saveCommission} disabled={saving} data-testid="save-commission-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
